import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { AppCard, RevealGroup } from '@/components/atoms';
import { type VehicleListing } from '@/services/vehicles/types';

import { VehicleCard } from './VehicleCard';

type Props = {
  vehicles: readonly VehicleListing[] | undefined;
  loading: boolean;
  skeletonCount?: number;
  /** Maximum columns at the widest breakpoint. */
  columns?: 3 | 4;
  headingLevel?: 'h2' | 'h3';
};

/** Mirrors the real card's rows, so nothing moves when the data lands. */
function CardSkeleton() {
  return (
    <AppCard aria-hidden sx={{ height: '100%' }}>
      <Skeleton variant="rectangular" sx={{ aspectRatio: '3 / 2', height: 'auto' }} />
      <Stack spacing={3} sx={{ p: { xs: 4, md: 5 } }}>
        <Box>
          <Skeleton width="80%" height={28} />
          <Skeleton width="45%" height={20} />
        </Box>
        <Skeleton width="90%" height={20} />
        <Box>
          <Skeleton width="55%" height={30} />
          <Skeleton width="70%" height={20} />
        </Box>
        <Skeleton variant="rounded" height={44} />
      </Stack>
    </AppCard>
  );
}

/**
 * A responsive grid of listing cards.
 *
 * Four-up now starts at `lg` rather than `xl`, so a laptop sees the fourth
 * car instead of leaving it to wrap onto a line of its own — the gap that
 * made a four-car featured row look broken on most screens.
 *
 * Tracks are `minmax(0, 1fr)` and not `1fr`. `1fr` is `minmax(auto, 1fr)`, and
 * that `auto` floor is the card's *min-content* width, which the `noWrap`
 * subtitle, spec and price lines hold well above the share of the row a card is
 * entitled to. The tracks then refuse to shrink and the last card in the row
 * runs past the container — a horizontal scrollbar on the whole page, and
 * columns of visibly different widths on the way there. A zero floor lets the
 * track win and those lines ellipsize, which is what `noWrap` asks for.
 *
 * `flex: 1` on the card is the other half: a flex item with no grow factor
 * stops at its max-content width, leaving the rest of its track empty.
 *
 * The whole grid is one `RevealGroup`: a single IntersectionObserver for the
 * section and a CSS-only stagger, rather than one observer per card.
 */
export function VehicleGrid({
  vehicles,
  loading,
  skeletonCount = 6,
  columns = 3,
  headingLevel,
}: Props) {
  const showingSkeletons = loading && !vehicles;
  const items = showingSkeletons
    ? Array.from({ length: skeletonCount }, () => null)
    : (vehicles ?? []);

  return (
    <RevealGroup
      component="ul"
      aria-busy={loading}
      disabled={showingSkeletons}
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'grid',
        gap: { xs: 4, lg: columns === 4 ? 3 : 4 },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: `repeat(${columns === 4 ? 3 : columns}, minmax(0, 1fr))`,
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
        opacity: loading && vehicles ? 0.6 : 1,
        transition: 'opacity 200ms ease',
      }}
    >
      {items.map((v, i) => (
        <Box
          component="li"
          key={v?.id ?? i}
          sx={{ display: 'flex', minWidth: 0, '& > *': { flex: 1, minWidth: 0 } }}
        >
          {v ? (
            <VehicleCard vehicle={v} {...(headingLevel ? { headingLevel } : {})} />
          ) : (
            <CardSkeleton />
          )}
        </Box>
      ))}
    </RevealGroup>
  );
}
